import { defineStore } from "pinia";

const updateUserLocation = async (): Promise<{ latitude: number; longitude: number } | null> => {
  try {
    if (!navigator.geolocation) return null;

    // ✅ Check permission status first
    const permission = await navigator.permissions.query({ name: "geolocation" as PermissionName });

    if (permission.state === "granted") {
      // ✅ Safe to fetch location (no popup)
      return new Promise((resolve) => {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const location = {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
            };

            localStorage.setItem("user_location", JSON.stringify(location));
            resolve(location);
          },
          () => resolve(null)
        );
      });
    }

    return null;

  } catch (err) {
    console.warn("Permission check failed:", err);
    return null;
  }
};
interface Address {
  name: string | null;
  phone: number | null;
  email: string | null;
  address_line1: string | null;
  address_line2: string | null;
  address_line3: string | null;
  area_code: number | null;
  city: string | null;
  state: string | null;
  payment_method: string | null;
  formatted_address: string | null;
  label: string | null;
  address_id: number | null;
}

interface Checkout {
  transaction_id: string | null;
  billing_address: Address | null;
  delivery_address: Address | null;
  selected_address: Address | null;
  response: { data: any; error: any };
  loader: boolean;
}

export const useCheckout = defineStore('checkout', {
  state: (): Checkout => ({
    transaction_id: null,
    billing_address: null,
    delivery_address: null,
    selected_address: null,
    response: { data: null, error: null },
    loader: false,
  }),
  actions: {
    setLoader(data: boolean) {
      this.loader = data;
    },
    setTransactionId(data: string) {
      this.transaction_id = data;
    },
    setDeliveryAddress(data: any) {
      const geolocation = useGeoLocation();
      geolocation.setOnlyDeliveryAddress(data);
      this.delivery_address = data;
    },
    setSelectedAddress(data: any) {
      this.selected_address = data;
    },
    updateContact(data: { name?: string; phone?: number }) {
      if (this.delivery_address) {
        this.delivery_address = {
          ...this.delivery_address,
          name: data.name || this.delivery_address.name,
          phone: data.phone || this.delivery_address.phone,
        };
      }
    },
    async checkoutUpdateAction(offer: any, tab_id: any) {
      let offerPayload = {} as { offers?: string };
      if (offer.offer_code !== null) {
        offerPayload.offers = encode(JSON.stringify(offer.offer_code.toUpperCase()));
      }

      const { reward = {} } = useCart();
      const rewardData = useReward();
      const { value: utmValue } = await $fetch<{ value?: string }>('/api/persist-cookie', {
        method: 'GET',
        query: { key: 'utm_parameters' },
      }).catch(() => ({ value: '{}' }));
      const utm = JSON.parse(utmValue || '{}');
      const cart = useCart();
      let payload = {
        ...offerPayload,
        utm,
        // user_location: freshLocation || {
        //   latitude: 0,
        //   longitude: 0,
        // },
      };
      if (reward && reward.rewardPoints > 0) {
        payload.reward = { provider: rewardData.provider, ...reward };
      }
      if(tab_id){
        payload.tab_id = tab_id
      }
      
      console.log('calling checkout update ');
      const { data: checkoutResponse, error } = await useAPIHandler(
        "post",
        "/checkout/update",
        {
          query: { transaction_id: this.transaction_id, count: 1 },
          body: payload,
          auth: true,
        }
      );
      return checkoutResponse;
    },
    async checkoutAction(offer: any, providerList: Array<any>, tab_id: any) {
      const { gtag } = useGtag();
      const auth = useAuth();
      const isAuthenticated = auth && auth.access_token;
      const userData = isAuthenticated
        ? {
            user_name: auth.user.first_name,
            user_phone: auth.user.phone,
            user_email: auth.user.email,
          }
        : {
            user_name: null,
            user_phone: null,
            user_email: null,
          };

      if (!this.transaction_id) {
        console.error("Transaction ID is required.");
        return;
      }
      const cart = useCart();
      const products = cart.items.map((cartItem) => ({
        product_id: cartItem.sku_id || '',
        name: cartItem.name || '',
        category: cartItem.category?.parent?.name || cartItem.category?.name || undefined,
        price: cartItem.price,
        quantity: cartItem.qty,
        currency: 'INR',
      }));

      const analyticsEvents = useAnalyticsEvents();
      useAnalytics(analyticsEvents.CHECKOUT_STARTED, {
        products,
        checkout_step: 1,
        cart_total_value: cart.itemTotal,
        currency: 'INR',
        transaction_id: this.transaction_id || undefined,
        items: providerList?.length || 0,
        discount_total: cart.offers?.offer_amount || undefined,
        offers_applied: cart.offers?.offer_code || undefined,
        coupon_applied: Boolean(cart.offers?.offer_code),
        coupon_code: cart.offers?.offer_code || undefined,
      });


        (window as any).fbq('track', 'InitiateCheckout', {
          value: 0, 
          currency: 'INR',
          num_items: providerList.length || 1, 
          content_type: 'product',
          contents: providerList.map((item) => ({
            id: item.id,
            quantity: item.quantity || 1,
          })),
        });
      

      this.loader = true;
      this.response.data = null;
      this.response.error = null;

      try {
        const location = useLocation();
        const geolocation = useGeoLocation();
        const OfferStatus = useCheckOffer();
        const firstDeliveryOffer = OfferStatus.delivery?.[0] || {};
        const discountOffer = OfferStatus.discount || [];
        const freebieOffer = OfferStatus.freebie || []
        const slabOffer = OfferStatus.slab || []
        const comboOffer = OfferStatus.combo || []
        const deliveryAddress = {
          door: this.delivery_address?.address_line1,
          building: this.delivery_address?.address_line2,
          locality: this.delivery_address?.address_line3,
          city: this.delivery_address?.city,
          state: this.delivery_address?.state,
          area_code: this.delivery_address?.area_code,
          contact: {
            name:
              this.delivery_address?.name &&
              this.delivery_address?.name != undefined
                ? this.delivery_address?.name
                : "",
            phone: `${
              this.delivery_address?.phone &&
              this.delivery_address?.phone != undefined
                ? this.delivery_address?.phone
                : ""
            }`,
            email: `${
              this.delivery_address?.email &&
              this.delivery_address?.email != undefined
                ? this.delivery_address?.email
                : ""
            }`,
          },
        };
        const payload = {
          location: {
            gps: `${ geolocation.deliveryAddress?.gps.latitude || location.gps.latitude},${ geolocation.deliveryAddress?.gps.longitude || location.gps.longitude}`,
            area_code: `${geolocation.deliveryAddress?.area_code || location.area_code}`,
          },
          city: { code: geolocation.deliveryAddress?.city?.code || location.city.code },
          billing: deliveryAddress,
          shipping: deliveryAddress,
          transaction_id: this.transaction_id,
        };
        let offerIds: string[] = [];
        if (
          firstDeliveryOffer.autoApply?.toLowerCase() === "no" &&
          firstDeliveryOffer.percent === 100
        ) {
          offerIds.push(firstDeliveryOffer.id);
        }
        freebieOffer.forEach(f => {
          if (f.autoApply?.toLowerCase() === "no" && f.remainingAmount === 0) {
            offerIds.push(f.id);
          }
        });
        discountOffer.forEach(f => {
          if (f.autoApply?.toLowerCase() === "no" && f.remainingAmount === 0) {
            offerIds.push(f.id);
          }
        });
        slabOffer.forEach(({ slabs }) => {
          slabs.forEach(slab => {
            const { current_qty, minQty, maxQty } = slab;
            const withinRange =
              current_qty >= minQty 
            if (withinRange && slab.autoApply?.toLowerCase() === "no") {
              offerIds.push(slab.id);
            }
          });
        });
        comboOffer.forEach(combo => {
          const itemSkuIds = cart.items.map(item => item.sku_id);
          const allMatched = combo.comboItems.every(sku => itemSkuIds.includes(sku));
        
          if (allMatched && combo.autoApply?.toLowerCase() === "no") {
            offerIds.push(combo.id);
          }
        });
        
        if (offerIds.length) {
          payload.offers = offerIds;
        }

        const { data: quoteData, error: checkoutError } = await useAPIHandler(
          "post",
          "/checkout",
          {
            body: payload,
            auth: true,
          }
        );

        if (checkoutError) {
          throw new Error(checkoutError.message);
        }

        if(quoteData?.success === false && quoteData?.message === 'Quote not found') {
          this.response.error = quoteData?.message;
          return
        }

        const { data, error } = await useCheckoutListener(
          this.transaction_id,
          providerList
        );
        const checkoutUpdateData = await this.checkoutUpdateAction(offer, tab_id);
        const { coupon } = useCartSummary();
        const { remove } = useCouponMaster();
        if(checkoutUpdateData.statusCode && checkoutUpdateData.statusCode === 400){
          useCartToast({
            description: checkoutUpdateData.message,
            timeout: 3000
          });
          this.loader = false;
          await remove(coupon.code)
          return;
        }
        this.response.data = data;
        this.response.error = error;
        if (error) {
          gtag("event", "checkout_api_event", {
            action: "Checkout Api Call",
            response: "Error",
            errorCode: error?.code,
            errorMessage: error?.message,
            ...userData,
          });
        }
      } catch (error: any) {
        console.error("Error in checkoutAction:", error.message);
        this.response.error = error.message;
        gtag("event", "checkout_api_event", {
          action: "checkout api call",
          response: "error",
          errorCode: error?.code,
          errorMessage: error?.message,
          ...userData,
        });
      } finally {
        this.loader = false;
      }
    },
    reset() {
      this.loader = false;
      this.response.data = null;
      this.response.error = null;
    },
  },
  persist: {
    storage: piniaPluginPersistedstate.localStorage(),
  },
});
